import React from 'react';
import '../../pages/setup/AssetSetupPage.css';

// Asset types + per-type settings. Shared by "Add asset" (setup) and "Edit
// asset" (asset detail) so both offer exactly the same options.

export interface AssetPlatformValues {
  platform_website: boolean;
  platform_mobile_app: boolean;
  platform_desktop_app: boolean;
  platform_iot: boolean;
  platform_endpoints: boolean;
  website_urls: string[];
  mobile_app_config: { app_store_url: string; play_store_url: string; package_name: string };
  desktop_app_config: { download_url: string; version: string; platform: string };
  iot_config: { device_type: string; api_endpoint: string; protocol: string };
}

export const emptyAssetPlatformValues = (): AssetPlatformValues => ({
  platform_website: false,
  platform_mobile_app: false,
  platform_desktop_app: false,
  platform_iot: false,
  platform_endpoints: false,
  website_urls: [''],
  mobile_app_config: { app_store_url: '', play_store_url: '', package_name: '' },
  desktop_app_config: { download_url: '', version: '', platform: '' },
  iot_config: { device_type: '', api_endpoint: '', protocol: '' },
});

// Build form values from an asset returned by the API (configs may be JSON strings).
export const assetToPlatformValues = (asset: any): AssetPlatformValues => {
  const parse = (v: any) => {
    if (!v) return {};
    if (typeof v === 'string') { try { return JSON.parse(v) || {}; } catch { return {}; } }
    return v;
  };
  const base = emptyAssetPlatformValues();
  const urls = Array.isArray(asset?.website_urls) ? asset.website_urls : parse(asset?.website_urls);
  return {
    platform_website: !!asset?.platform_website,
    platform_mobile_app: !!asset?.platform_mobile_app,
    platform_desktop_app: !!asset?.platform_desktop_app,
    platform_iot: !!asset?.platform_iot,
    platform_endpoints: !!asset?.platform_endpoints,
    website_urls: Array.isArray(urls) && urls.length ? urls : [''],
    mobile_app_config: { ...base.mobile_app_config, ...parse(asset?.mobile_app_config) },
    desktop_app_config: { ...base.desktop_app_config, ...parse(asset?.desktop_app_config) },
    iot_config: { ...base.iot_config, ...parse(asset?.iot_config) },
  };
};

export const hasAnyPlatform = (v: AssetPlatformValues) =>
  v.platform_website || v.platform_mobile_app || v.platform_desktop_app || v.platform_iot || v.platform_endpoints;

const PLATFORMS: { key: keyof AssetPlatformValues; label: string; icon: string; hint: string }[] = [
  { key: 'platform_website', label: 'Website', icon: 'bi-globe', hint: 'Analytics, uptime, security & SEO' },
  { key: 'platform_mobile_app', label: 'Mobile App', icon: 'bi-phone', hint: 'User lifecycle & funnels via SDK' },
  { key: 'platform_desktop_app', label: 'Desktop App', icon: 'bi-window-desktop', hint: 'Your own desktop software' },
  { key: 'platform_endpoints', label: 'Endpoint Devices', icon: 'bi-laptop', hint: 'Company laptops & desktops' },
  { key: 'platform_iot', label: 'IoT Device', icon: 'bi-cpu', hint: 'Sensors, gateways' },
];

interface Props {
  value: AssetPlatformValues;
  onChange: (next: AssetPlatformValues) => void;
  disabled?: boolean;
  /** Shown in the endpoint-devices section, e.g. a link to the devices of this asset. */
  endpointsFooter?: React.ReactNode;
}

const AssetPlatformFields: React.FC<Props> = ({ value, onChange, disabled, endpointsFooter }) => {
  const set = (patch: Partial<AssetPlatformValues>) => onChange({ ...value, ...patch });
  const setConfig = <K extends 'mobile_app_config' | 'desktop_app_config' | 'iot_config'>(key: K, field: string, v: string) =>
    set({ [key]: { ...value[key], [field]: v } } as any);

  const setUrl = (i: number, raw: string) => {
    let v = raw;
    if (v && !v.startsWith('http://') && !v.startsWith('https://')) v = 'https://' + v;
    const urls = [...value.website_urls];
    urls[i] = v;
    set({ website_urls: urls });
  };

  return (
    <>
      <div className="form-section">
        <h3 className="section-title">
          <i className="bi bi-layers me-2"></i>
          Asset Types <span className="text-danger">*</span>
        </h3>
        <p className="text-muted small mb-3">Pick everything this asset includes — you can change it any time.</p>
        <div className="platform-grid">
          {PLATFORMS.map((p) => (
            <label key={p.key} className="platform-card" title={p.hint}>
              <input
                type="checkbox"
                checked={!!value[p.key]}
                onChange={(e) => set({ [p.key]: e.target.checked } as any)}
                disabled={disabled}
              />
              <div className="platform-content">
                <i className={`bi ${p.icon} platform-icon`}></i>
                <span className="platform-name">{p.label}</span>
                <span className="text-muted" style={{ fontSize: '0.72rem', lineHeight: 1.2 }}>{p.hint}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {value.platform_website && (
        <div className="form-section">
          <h3 className="section-title"><i className="bi bi-globe me-2"></i>Website Configuration</h3>
          {value.website_urls.map((url, i) => (
            <div key={i} className="mb-3">
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-link-45deg"></i></span>
                <input
                  type="url"
                  className="form-control"
                  placeholder="example.com (https:// will be added automatically)"
                  value={url}
                  onChange={(e) => setUrl(i, e.target.value)}
                  onFocus={(e) => { if (!e.target.value) setUrl(i, 'https://'); }}
                  disabled={disabled}
                />
                {value.website_urls.length > 1 && (
                  <button type="button" className="btn btn-outline-danger" disabled={disabled}
                    onClick={() => set({ website_urls: value.website_urls.filter((_, j) => j !== i) })}>
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-outline-primary btn-sm" disabled={disabled}
            onClick={() => set({ website_urls: [...value.website_urls, ''] })}>
            <i className="bi bi-plus-circle me-1"></i>Add Another URL
          </button>
        </div>
      )}

      {value.platform_mobile_app && (
        <div className="form-section">
          <h3 className="section-title"><i className="bi bi-phone me-2"></i>Mobile App Configuration</h3>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">App Store URL</label>
              <input type="url" className="form-control" placeholder="https://apps.apple.com/..." disabled={disabled}
                value={value.mobile_app_config.app_store_url} onChange={(e) => setConfig('mobile_app_config', 'app_store_url', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Play Store URL</label>
              <input type="url" className="form-control" placeholder="https://play.google.com/..." disabled={disabled}
                value={value.mobile_app_config.play_store_url} onChange={(e) => setConfig('mobile_app_config', 'play_store_url', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Package Name</label>
              <input type="text" className="form-control" placeholder="com.example.app" disabled={disabled}
                value={value.mobile_app_config.package_name} onChange={(e) => setConfig('mobile_app_config', 'package_name', e.target.value)} />
            </div>
          </div>
          <div className="small text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Track installs, sign-ups and purchases from <strong>App Analytics → Tracking Plan &amp; SDK</strong> after saving.
          </div>
        </div>
      )}

      {value.platform_desktop_app && (
        <div className="form-section">
          <h3 className="section-title"><i className="bi bi-window-desktop me-2"></i>Desktop App Configuration</h3>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Download URL</label>
              <input type="url" className="form-control" placeholder="https://example.com/download" disabled={disabled}
                value={value.desktop_app_config.download_url} onChange={(e) => setConfig('desktop_app_config', 'download_url', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Version</label>
              <input type="text" className="form-control" placeholder="1.0.0" disabled={disabled}
                value={value.desktop_app_config.version} onChange={(e) => setConfig('desktop_app_config', 'version', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Platform</label>
              <select className="form-select" disabled={disabled}
                value={value.desktop_app_config.platform} onChange={(e) => setConfig('desktop_app_config', 'platform', e.target.value)}>
                <option value="">Select Platform</option>
                <option value="windows">Windows</option>
                <option value="macos">macOS</option>
                <option value="linux">Linux</option>
                <option value="cross-platform">Cross-platform</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {value.platform_endpoints && (
        <div className="form-section">
          <h3 className="section-title"><i className="bi bi-laptop me-2"></i>Endpoint Devices</h3>
          <p className="small text-muted mb-2">
            Group your company laptops and desktops under this asset. Each device runs the CloudAyn agent or the
            CloudAyn Protection app and reports health, threats and scans here. You can move devices between assets
            at any time.
          </p>
          {endpointsFooter}
        </div>
      )}

      {value.platform_iot && (
        <div className="form-section">
          <h3 className="section-title"><i className="bi bi-cpu me-2"></i>IoT Device Configuration</h3>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Device Type</label>
              <input type="text" className="form-control" placeholder="Smart Sensor, Gateway, etc." disabled={disabled}
                value={value.iot_config.device_type} onChange={(e) => setConfig('iot_config', 'device_type', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">API Endpoint</label>
              <input type="url" className="form-control" placeholder="https://api.example.com/iot" disabled={disabled}
                value={value.iot_config.api_endpoint} onChange={(e) => setConfig('iot_config', 'api_endpoint', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Protocol</label>
              <select className="form-select" disabled={disabled}
                value={value.iot_config.protocol} onChange={(e) => setConfig('iot_config', 'protocol', e.target.value)}>
                <option value="">Select Protocol</option>
                <option value="http">HTTP/HTTPS</option>
                <option value="mqtt">MQTT</option>
                <option value="websocket">WebSocket</option>
                <option value="coap">CoAP</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetPlatformFields;
